"use client";

import { useState } from "react";
import { Check, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import type { ProductFAQ } from "@/types";
import { useTranslations } from "next-intl";

interface ProductFAQsProps {
  faqs: ProductFAQ[];
}

interface FAQAccordionItemProps {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

function FAQAccordionItem({
  question,
  answer,
  defaultOpen = true,
}: FAQAccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-blue-500 shrink-0" />
          <span className="font-medium text-gray-900">{question}</span>
        </div>
        <ChevronUp
          className={cn(
            "h-5 w-5 text-gray-400 transition-transform duration-200 shrink-0",
            !isOpen && "rotate-180",
          )}
        />
      </button>

      {/* Content - 회색 배경 */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          isOpen ? "max-h-125 pb-4" : "max-h-0",
        )}
      >
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="prose prose-sm prose-zinc max-w-none prose-headings:tracking-tight prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1.5 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline prose-li:my-0.5 prose-ul:my-1.5 prose-ol:my-1.5 wrap-break-word">
            <ReactMarkdown>{answer}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductFAQs({ faqs }: ProductFAQsProps) {
  const t = useTranslations("product.faqs");

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
      </div>

      {/* FAQ Items */}
      <div>
        {faqs.map((faq) => (
          <FAQAccordionItem
            key={faq.id}
            question={faq.question}
            answer={faq.answer}
            defaultOpen={true}
          />
        ))}
      </div>
    </div>
  );
}
